import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { statsAPI, adminAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { 
    Scale, 
    Trophy, 
    Target, 
    Zap, 
    BookOpen, 
    BarChart3,
    Award,
    Flame,
    ChevronRight,
    LogOut,
    Play,
    Users,
    TrendingUp,
    Crown,
    Shield
} from "lucide-react";
import { toast } from "sonner";
import { BrandLogo } from "../components/Brand";

const ACHIEVEMENTS = {
    "first_game": { name: "First Steps", icon: Trophy, color: "text-[#299dce]" },
    "perfect_round": { name: "Perfect Round", icon: Crown, color: "text-yellow-400" },
    "streak_5": { name: "On Fire", icon: Flame, color: "text-orange-400" },
    "streak_10": { name: "Unstoppable", icon: Zap, color: "text-cyan-400" },
    "games_10": { name: "Dedicated", icon: Target, color: "text-emerald-400" },
    "games_50": { name: "Committed", icon: Award, color: "text-[#299dce]" },
    "score_1000": { name: "Rising Star", icon: TrendingUp, color: "text-[#299dce]" },
    "score_5000": { name: "Legal Eagle", icon: Crown, color: "text-amber-400" },
    "all_subjects": { name: "Well Rounded", icon: BookOpen, color: "text-[#299dce]" },
    "speed_demon": { name: "Speed Demon", icon: Zap, color: "text-red-400" },
};

export default function DashboardPage() {
    const { user, logout, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [seeding, setSeeding] = useState(false);
    
    useEffect(() => {
        loadStats();
    }, []);
    
    const loadStats = async () => {
        try {
            const response = await statsAPI.getMyStats();
            setStats(response.data);
        } catch (error) {
            console.error("Failed to load stats:", error);
        } finally {
            setLoading(false);
        }
    };
    
    const handleSeedQuestions = async () => {
        setSeeding(true);
        try {
            await adminAPI.seedQuestions();
            toast.success("Questions seeded successfully!");
        } catch (error) {
            toast.error("Failed to seed questions");
        } finally {
            setSeeding(false);
        }
    };
    
    const handleLogout = () => {
        logout();
        navigate("/");
    };
    
    const xpProgress = stats ? ((stats.xp % 500) / 500) * 100 : 0;
    
    return (
        <div className="min-h-screen bg-background">
            {/* Navigation */}
            <nav className="sticky top-0 z-50 glass border-b border-slate-800 px-6 py-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <BrandLogo linkTo="/dashboard" />
                    
                    <div className="flex items-center gap-2 sm:gap-4">
                        <Link to="/leaderboard">
                            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" data-testid="nav-leaderboard">
                                <Trophy className="w-4 h-4 sm:mr-2" />
                                <span className="hidden sm:inline">Leaderboard</span>
                            </Button>
                        </Link>
                        <Link to="/profile">
                            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" data-testid="nav-profile">
                                <Award className="w-4 h-4 sm:mr-2" />
                                <span className="hidden sm:inline">Profile</span>
                            </Button>
                        </Link>
                        {user?.is_admin && (
                            <Link to="/admin">
                                <Button variant="ghost" size="sm" className="text-amber-400 hover:text-amber-300">
                                    <Shield className="w-4 h-4 sm:mr-2" />
                                    <span className="hidden sm:inline">Admin</span>
                                </Button>
                            </Link>
                        )}
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={handleLogout}
                            className="text-slate-400 hover:text-white"
                            data-testid="nav-logout"
                        >
                            <LogOut className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </nav>
            
            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Welcome Header */}
                <div className="mb-8">
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="font-heading text-3xl sm:text-4xl font-bold text-white mb-2"
                    >
                        Welcome back, <span className="gradient-text">{user?.username}</span>
                    </motion.h1>
                    <p className="text-slate-400">Ready to dominate the bar exam?</p>
                </div>
                
                {/* Quick Actions */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
                >
                    <Link to="/play?mode=quick_play" className="block">
                        <Card className="glass border-slate-800 rounded-2xl hover:border-primary/50 transition-colors cursor-pointer group h-full" data-testid="quick-play-card">
                            <CardContent className="p-6 flex flex-col items-center text-center">
                                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mb-4 group-hover:glow-primary transition-shadow">
                                    <Zap className="w-6 h-6 text-primary" />
                                </div>
                                <h3 className="font-heading font-semibold text-white mb-1">Quick Play</h3>
                                <p className="text-slate-400 text-sm">10 random questions</p>
                            </CardContent>
                        </Card>
                    </Link>
                    
                    <Link to="/play?mode=practice" className="block">
                        <Card className="glass border-slate-800 rounded-2xl hover:border-secondary/50 transition-colors cursor-pointer group h-full" data-testid="practice-card">
                            <CardContent className="p-6 flex flex-col items-center text-center">
                                <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center mb-4 group-hover:glow-secondary transition-shadow">
                                    <Target className="w-6 h-6 text-secondary" />
                                </div>
                                <h3 className="font-heading font-semibold text-white mb-1">Practice Mode</h3>
                                <p className="text-slate-400 text-sm">No timer, learn at your pace</p>
                            </CardContent>
                        </Card>
                    </Link>
                    
                    <Link to="/study" className="block">
                        <Card className="glass border-slate-800 rounded-2xl hover:border-emerald-500/50 transition-colors cursor-pointer group h-full" data-testid="study-card">
                            <CardContent className="p-6 flex flex-col items-center text-center">
                                <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(16,185,129,0.4)] transition-shadow">
                                    <BookOpen className="w-6 h-6 text-emerald-400" />
                                </div>
                                <h3 className="font-heading font-semibold text-white mb-1">Study Mode</h3>
                                <p className="text-slate-400 text-sm">Flashcards & review</p>
                            </CardContent>
                        </Card>
                    </Link>
                    
                    <Link to="/leaderboard" className="block">
                        <Card className="glass border-slate-800 rounded-2xl hover:border-amber-500/50 transition-colors cursor-pointer group h-full" data-testid="leaderboard-card">
                            <CardContent className="p-6 flex flex-col items-center text-center">
                                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(245,158,11,0.4)] transition-shadow">
                                    <Trophy className="w-6 h-6 text-amber-400" />
                                </div>
                                <h3 className="font-heading font-semibold text-white mb-1">Leaderboard</h3>
                                <p className="text-slate-400 text-sm">See top players</p>
                            </CardContent>
                        </Card>
                    </Link>
                </motion.div>
                
                {/* Stats Grid */}
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : stats ? (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        {/* Level & XP Card */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="lg:col-span-4"
                        >
                            <Card className="glass border-slate-800 rounded-2xl h-full" data-testid="level-card">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-lg text-white flex items-center gap-2">
                                        <TrendingUp className="w-5 h-5 text-primary" />
                                        Your Progress
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-center mb-4">
                                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/20 border-2 border-primary mb-2">
                                            <span className="font-heading text-3xl font-bold text-primary">{stats.level}</span>
                                        </div>
                                        <p className="text-slate-400 text-sm">Level</p>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-400">XP Progress</span>
                                            <span className="text-white font-mono">{stats.xp % 500} / 500</span>
                                        </div>
                                        <Progress value={xpProgress} className="h-2 bg-slate-800" />
                                        <p className="text-slate-500 text-xs text-center">
                                            {stats.xp_to_next_level} XP to next level
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                        
                        {/* Stats Overview */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="lg:col-span-8"
                        >
                            <Card className="glass border-slate-800 rounded-2xl h-full" data-testid="stats-card">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-lg text-white flex items-center gap-2">
                                        <BarChart3 className="w-5 h-5 text-secondary" />
                                        Statistics
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                        <div className="text-center p-4 rounded-xl bg-slate-800/40">
                                            <p className="font-heading text-2xl sm:text-3xl font-bold text-white">{stats.total_score.toLocaleString()}</p>
                                            <p className="text-slate-400 text-sm">Total Score</p>
                                        </div>
                                        <div className="text-center p-4 rounded-xl bg-slate-800/40">
                                            <p className="font-heading text-2xl sm:text-3xl font-bold text-white">{stats.games_played}</p>
                                            <p className="text-slate-400 text-sm">Games Played</p>
                                        </div>
                                        <div className="text-center p-4 rounded-xl bg-slate-800/40">
                                            <p className="font-heading text-2xl sm:text-3xl font-bold text-emerald-400">{stats.accuracy}%</p>
                                            <p className="text-slate-400 text-sm">Accuracy</p>
                                        </div>
                                        <div className="text-center p-4 rounded-xl bg-slate-800/40">
                                            <p className="font-heading text-2xl sm:text-3xl font-bold text-orange-400">{stats.best_streak}</p>
                                            <p className="text-slate-400 text-sm">Best Streak</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                        
                        {/* Achievements */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="lg:col-span-6"
                        >
                            <Card className="glass border-slate-800 rounded-2xl h-full" data-testid="achievements-card">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-lg text-white flex items-center gap-2">
                                        <Award className="w-5 h-5 text-amber-400" />
                                        Achievements
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {stats.achievements.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {stats.achievements.map((id) => {
                                                const achievement = ACHIEVEMENTS[id];
                                                if (!achievement) return null;
                                                const Icon = achievement.icon;
                                                return (
                                                    <Badge
                                                        key={id}
                                                        className={`bg-slate-800/60 border border-slate-700 px-3 py-1.5 ${achievement.color}`}
                                                    >
                                                        <Icon className="w-3 h-3 mr-1" />
                                                        {achievement.name}
                                                    </Badge>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-slate-500 text-sm text-center py-4">
                                            Play games to unlock achievements!
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                        </motion.div>
                        
                        {/* Subjects Progress */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                            className="lg:col-span-6"
                        >
                            <Card className="glass border-slate-800 rounded-2xl h-full" data-testid="subjects-card">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-lg text-white flex items-center gap-2">
                                        <BookOpen className="w-5 h-5 text-emerald-400" />
                                        Subjects ({stats.subjects_played.length}/8)
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-2 gap-2">
                                        {Object.entries(stats.subject_stats).map(([subject, data]) => (
                                            <div 
                                                key={subject}
                                                className={`px-3 py-2 rounded-lg text-xs ${
                                                    data.played 
                                                        ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400"
                                                        : "bg-slate-800/40 border border-slate-700/50 text-slate-500"
                                                }`}
                                            >
                                                {subject}
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    </div>
                ) : null}
                
                {/* Admin Seed Button (only for admins) */}
                {user?.is_admin && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6 }}
                        className="mt-8 text-center"
                    >
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSeedQuestions}
                            disabled={seeding}
                            className="text-slate-400 border-slate-700 hover:border-primary hover:text-primary"
                            data-testid="seed-questions-btn"
                        >
                            {seeding ? "Seeding..." : "Seed Sample Questions (Admin)"}
                        </Button>
                    </motion.div>
                )}
            </main>
        </div>
    );
}
